import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SearchSelect } from "@/components/SearchSelect";
import { api, apiError, eur, dateFmt } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Th, Td, SortTh } from "@/components/common";
import { toast } from "sonner";
import { useI18n, intlTag } from "@/i18n";
import MesseSettleTable from "@/components/messe/MesseSettleTable";
import { Plus, Trash2, Truck, Printer, ClipboardCheck, Pencil, Check, Loader2, AlertTriangle, Search } from "lucide-react";

const AUTOSAVE_MS = 800;

export default function MesseTakelist({ fairId, fairName, canEdit, closed, variants, bundles, messeBundles, onChanged }) {
  const [stock, setStock] = useState(null);
  const [lines, setLines] = useState([]);
  const [entries, setEntries] = useState({});
  const [busy, setBusy] = useState(false);
  const [availMap, setAvailMap] = useState({});
  // Erfassungsentwurf: serverseitig gespeichert, damit Eingaben einen Seitenwechsel überleben.
  const [draft, setDraft] = useState({ version: null, updatedAt: null, by: null });
  const [q, setQ] = useState("");
  // Sortierung: `order` ist eine EINGEFRORENE Reihenfolge, die nur beim Klick auf eine
  // Spaltenüberschrift neu berechnet wird. Sonst würden Zeilen beim Tippen in „Verkauft"/„Zurück"
  // unter dem Cursor wegspringen.
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [order, setOrder] = useState(null);
  const { t, tp, locale } = useI18n();
  const [saveState, setSaveState] = useState("idle"); // idle | dirty | saving | saved | error
  // Erst nach dem Hydrieren darf bedient werden – sonst würde hydrate() lokale Eingaben verwerfen.
  const [hydrated, setHydrated] = useState(false);
  const timerRef = useRef(null);
  const latestRef = useRef({ entries: {}, lines: [], version: null });
  const hydratedRef = useRef(false);
  const reconciledRef = useRef(false);

  useEffect(() => { api.get("/inventory").then((r) => setAvailMap(Object.fromEntries((r.data || []).map((x) => [x.variant_id, x.available])))).catch(() => {}); }, []);
  const optLabel = (kind, x) => kind === "variant"
    ? `${x.product_name} · ${x.sku} · verfügbar: ${availMap[x.id] ?? 0}`
    : `${x.name} · ${x.sku} · verfügbar: ${x.sellable ?? 0}`;

  // Entwurf in die Formularfelder zurückschreiben. `soldT/retT` werden gesetzt, damit die
  // Auto-Ergänzung des Gegenfeldes einen wiederhergestellten Stand nicht überschreibt.
  const hydrate = useCallback((d) => {
    const e = {};
    (d?.items || []).forEach((it) => {
      e[`${it.kind}:${it.ref}`] = { sold: String(it.sold ?? 0), returned: String(it.returned ?? 0), soldT: true, retT: true };
    });
    setEntries(e);
    setLines((d?.take_lines || []).map((l) => ({ uid: crypto.randomUUID(), kind: l.kind || "variant", ref: l.ref || "", quantity: l.quantity ?? "1" })));
    setDraft({ version: d?.version ?? null, updatedAt: d?.updated_at || null, by: d?.updated_by_email || null });
    latestRef.current = { entries: e, lines: d?.take_lines || [], version: d?.version ?? null };
    setSaveState(d ? "saved" : "idle");
  }, []);

  const load = useCallback(() => api.get(`/trade-fairs/${fairId}/messe-stock`).then((r) => {
    setStock(r.data);
    return r.data;
  }).catch(() => { setStock(null); return null; }), [fairId]);

  useEffect(() => {
    hydratedRef.current = false;
    setHydrated(false);
    load().then((d) => { hydrate(d?.settle_draft || null); hydratedRef.current = true; setHydrated(true); });
  }, [load, hydrate]);

  const payloadOf = (e, ls) => ({
    items: Object.entries(e)
      .filter(([, v]) => (v?.sold ?? "") !== "" || (v?.returned ?? "") !== "")
      .map(([k, v]) => {
        const i = k.indexOf(":");
        return { kind: k.slice(0, i), ref: k.slice(i + 1), sold: Number(v.sold) || 0, returned: Number(v.returned) || 0 };
      }),
    take_lines: ls.map((l) => ({ kind: l.kind || "variant", ref: l.ref || "", quantity: String(l.quantity ?? "") })),
  });

  const saveDraft = useCallback(async (e, ls) => {
    if (!canEdit || closed || stock?.reconciled_at) return;
    setSaveState("saving");
    try {
      const r = await api.put(`/trade-fairs/${fairId}/settle-draft`, {
        ...payloadOf(e, ls), expected_version: latestRef.current.version });
      latestRef.current.version = r.data.draft.version;
      setDraft({ version: r.data.draft.version, updatedAt: r.data.draft.updated_at, by: r.data.draft.updated_by_email });
      setSaveState("saved");
    } catch (err) {
      if (err?.response?.status === 409) {
        const d = err.response.data?.detail;
        toast.error(d?.message || t("me.draftConflict"));
        hydrate(d?.draft || null);
        return;
      }
      setSaveState("error");
      toast.error(apiError(err));
    }
  }, [canEdit, closed, stock, fairId, hydrate, t]);

  // Entprelltes Auto-Speichern nach der letzten Eingabe.
  const queueSave = useCallback((e, ls) => {
    latestRef.current = { ...latestRef.current, entries: e, lines: ls };
    setSaveState("dirty");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => saveDraft(e, ls), AUTOSAVE_MS);
  }, [saveDraft]);

  // Noch nicht gespeicherter Stand: Browser-Warnung + Sicherung beim Verlassen der Seite.
  useEffect(() => {
    const warn = (ev) => {
      if (saveState === "dirty" || saveState === "saving" || saveState === "error") {
        ev.preventDefault(); ev.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [saveState]);

  useEffect(() => () => {
    // Beim Verlassen der Seite den letzten Stand noch abschicken (kein Verlust durch Debounce).
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!hydratedRef.current || !canEdit || closed || reconciledRef.current) return;
    const { entries: e, lines: ls, version } = latestRef.current;
    const p = payloadOf(e, ls);
    // Nichts erfasst und kein Entwurf vorhanden -> keinen leeren Entwurf anlegen.
    if (version == null && !p.items.length && !p.take_lines.length) return;
    api.put(`/trade-fairs/${fairId}/settle-draft`, { ...p, expected_version: version }).catch(() => {});
  }, [fairId, canEdit, closed]);

  const addLine = () => { const l = [...lines, { uid: crypto.randomUUID(), kind: "variant", ref: "", quantity: 1 }]; setLines(l); queueSave(entries, l); };
  const updLine = (i, k, v) => { const l = [...lines]; l[i] = { ...l[i], [k]: v }; if (k === "kind") l[i].ref = ""; setLines(l); queueSave(entries, l); };
  const rmLine = (i) => { const l = lines.filter((_, j) => j !== i); setLines(l); queueSave(entries, l); };

  const book = async () => {
    const payload = { lines: lines.filter((l) => l.ref && Number(l.quantity) > 0).map((l) => ({ kind: l.kind, ref: l.ref, quantity: Number(l.quantity) })) };
    if (!payload.lines.length) { toast.error(t("me.noValidLines")); return; }
    setBusy(true);
    try {
      await api.post(`/trade-fairs/${fairId}/take`, payload);
      toast.success(t("me.takeBooked"));
      setLines([]);
      await saveDraft(entries, []);
      const d = await load();
      // Neu mitgenommene Positionen erscheinen jetzt in der Abrechnung – Entwurf beibehalten.
      if (d?.settle_draft) latestRef.current.version = d.settle_draft.version;
      onChanged?.();
    }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const key = (it) => `${it.kind}:${it.ref}`;
  const val = (it, f) => Number(entries[key(it)]?.[f]) || 0;
  const enter = (it, field, raw) => {
    // Verkauft + Zurück dürfen "Auf Messe" nie übersteigen. Das andere Feld wird automatisch
    // ergänzt, solange es NICHT manuell geändert wurde; danach wird das aktuelle Feld so gekappt,
    // dass die Summe <= "Auf Messe" bleibt (Schwund = Rest, immer >= 0).
    const k = key(it); const cap = it.on_fair || 0; const cur = entries[k] || {};
    const other = field === "sold" ? "returned" : "sold";
    const touchedThis = field === "sold" ? "soldT" : "retT";
    const touchedOther = field === "sold" ? "retT" : "soldT";
    const otherVal = Number(cur[other]) || 0;
    const limit = cur[touchedOther] ? Math.max(0, cap - otherVal) : cap;
    const n = raw === "" ? "" : Math.max(0, Math.min(limit, Math.floor(Number(raw)) || 0));
    const next = { ...cur, [field]: n === "" ? "" : String(n), [touchedThis]: true };
    if (!cur[touchedOther]) next[other] = raw === "" ? "" : String(Math.max(0, cap - n));
    const e = { ...entries, [k]: next };
    setEntries(e);
    queueSave(e, lines);
  };
  const schwund = (it) => Math.max(0, (it.on_fair || 0) - val(it, "sold") - val(it, "returned"));
  const overbooked = (it) => val(it, "sold") + val(it, "returned") > (it.on_fair || 0);

  const settle = async (close) => {
    const items = stock?.items || [];
    if (items.some(overbooked)) { toast.error(t("me.overbook")); return; }
    const totSold = items.reduce((s, it) => s + val(it, "sold"), 0);
    const totRet = items.reduce((s, it) => s + val(it, "returned"), 0);
    const totSchwund = items.reduce((s, it) => s + schwund(it), 0);
    if (!window.confirm(t("me.settleConfirm", { sold: totSold, ret: totRet, shrink: totSchwundQ }))) return;
    const payload = { close, items: items.map((it) => ({ kind: it.kind, ref: it.ref, sold: val(it, "sold"), returned: val(it, "returned") })) };
    setBusy(true);
    setBusy(true);
    try {
      const r = await api.post(`/trade-fairs/${fairId}/settle`, payload);
      toast.success(`Abrechnung gebucht · ${r.data.sales} Verkaufsposten · Schwund ${eur(r.data.shrinkage_value || 0)}`);
      // Entwurf wurde serverseitig mit der Buchung entfernt.
      if (timerRef.current) clearTimeout(timerRef.current);
      setEntries({}); setLines([]);
      setDraft({ version: null, updatedAt: null, by: null });
      latestRef.current = { entries: {}, lines: [], version: null };
      setSaveState("idle");
      load(); onChanged?.();
    }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const discardDraft = async () => {
    if (!window.confirm(t("me.discardDraft"))) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setBusy(true);
    try {
      await api.delete(`/trade-fairs/${fairId}/settle-draft`);
      hydrate(null);
      toast.success(t("me.draftDiscarded"));
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const adjustTake = async (it, qty) => {
    setBusy(true);
    try {
      await api.patch(`/trade-fairs/${fairId}/takelist`, { kind: it.kind, ref: it.ref, quantity: qty });
      toast.success(qty === 0 ? t("me.takeRemoved") : t("me.takeQtyChanged"));
      load(); onChanged?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };  const removeTake = (it) => {
    if (window.confirm(t("me.removeTakeConfirm", { name: it.name, qty: it.taken }))) adjustTake(it, 0);
  };
  const editTake = (it) => {
    const v = window.prompt(t("me.newTakeQtyPrompt", { name: it.name }), String(it.taken));
    if (v == null) return;
    const q = Math.floor(Number(v));
    if (!Number.isFinite(q) || q < 0) { toast.error(t("errors.invalidQuantity")); return; }
    if (q === it.taken) return;
    adjustTake(it, q);
  };

  // Ist-Werte kommen ausschließlich vom Server (Abrechnungs-Snapshot). Vor der Abrechnung gilt
  // die Soll-/Erfassungssicht aus den Entwurfsfeldern.
  const settled = !!stock?.reconciled_at;
  const soldOf = (it) => (settled ? Number(it.sold_actual) || 0 : val(it, "sold"));
  const retOf = (it) => (settled ? Number(it.returned_actual) || 0 : val(it, "returned"));
  const schwundOf = (it) => (settled ? Number(it.shrinkage_actual) || 0 : schwund(it));
  const revOf = (it) => (settled ? Number(it.revenue_actual) || 0 : val(it, "sold") * (it.price_net || 0));
  const shrValOf = (it) => (settled ? Number(it.shrinkage_value_actual) || 0 : schwund(it) * (it.cost_net || 0));
  const dbOf = (it) => (settled ? Number(it.margin_actual) || 0 : val(it, "sold") * ((it.price_net || 0) - (it.cost_net || 0)));
  const deDate = (iso) => (iso ? dateFmt(iso) : "—");

  const printList = () => {
    const items = stock?.items || [];
    const w = window.open("", "_blank");
    if (!w) { toast.error(t("me.popupBlocked")); return; }
    const doc = w.document;
    const el = (tag, opts = {}) => {
      const n = doc.createElement(tag);
      if (opts.text != null) n.textContent = String(opts.text);
      if (opts.align) n.style.textAlign = opts.align;
      if (opts.colSpan) n.colSpan = opts.colSpan;
      return n;
    };
    const title = settled ? t("me.finalReport") : t("me.takeList");
    doc.title = `${title} – ${fairName || ""}`;
    const style = el("style", { text: "body{font-family:Arial,sans-serif;padding:24px;color:#111}h1{font-size:18px;margin:0 0 4px}table{width:100%;border-collapse:collapse;margin-top:12px}th,td{border:1px solid #999;padding:6px 8px;font-size:12px}th{background:#eee;text-align:left}tfoot td{font-weight:bold;background:#f4f4f4}.meta{font-size:12px;color:#555}@media print{button{display:none}}" });
    doc.head.appendChild(style);

    doc.body.appendChild(el("h1", { text: `${title} – ${fairName || ""}` }));
    const meta = el("div");
    meta.className = "meta";
    if (settled) {
      // Fachlich maßgeblich ist das Abrechnungsdatum. Das Druckdatum wird getrennt ausgewiesen.
      meta.appendChild(el("div", { text: `${t("me.settleDate")}: ${deDate(stock?.reconciled_at)}` }));
      meta.appendChild(el("div", { text: `${t("me.settledBy")}: ${stock?.reconciled_by_email || "—"}` }));
      if (stock?.closed_at) meta.appendChild(el("div", { text: `${t("me.fairClosed")}: ${deDate(stock.closed_at)}` }));
      meta.appendChild(el("div", { text: `${t("me.printDate")}: ${dateFmt(new Date().toISOString())}` }));
    } else {
      meta.appendChild(el("div", { text: `${t("me.printDate")}: ${dateFmt(new Date().toISOString())}` }));
    }
    doc.body.appendChild(meta);

    const cols = settled
      ? [["SKU"], [t("op.item")], [t("me.taken"), "right"], [t("me.sold"), "right"], [t("me.returned"), "right"],
         [t("me.shrinkage"), "right"], [t("common.revenueNet"), "right"], [t("me.shrinkageValue"), "right"], [t("common.margin"), "right"]]
      : [["SKU"], [t("op.item")], [t("me.taken"), "right"], [t("me.sold")], [t("me.returned")], [t("me.shrinkage")]];
    const table = el("table");
    const thead = el("thead"), htr = el("tr");
    cols.forEach(([t, a]) => htr.appendChild(el("th", { text: t, align: a })));
    thead.appendChild(htr); table.appendChild(thead);

    const tbody = el("tbody");
    if (items.length) {
      items.forEach((it) => {
        const tr = el("tr");
        tr.appendChild(el("td", { text: it.sku }));
        tr.appendChild(el("td", { text: it.name }));
        tr.appendChild(el("td", { text: it.taken, align: "right" }));
        if (settled) {
          [[soldOf(it)], [retOf(it)], [schwundOf(it)]].forEach(([v]) => tr.appendChild(el("td", { text: v, align: "right" })));
          [revOf(it), shrValOf(it), dbOf(it)].forEach((v) => tr.appendChild(el("td", { text: eur(v), align: "right" })));
        } else {
          tr.appendChild(el("td")); tr.appendChild(el("td")); tr.appendChild(el("td"));
        }
        tbody.appendChild(tr);
      });
    } else {
      const tr = el("tr");
      tr.appendChild(el("td", { text: t("me.noTakeBooked"), colSpan: cols.length }));
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);

    if (settled && items.length) {
      const add = (row, vals) => vals.forEach(([t, a]) => row.appendChild(el("td", { text: t, align: a })));
      const tfoot = el("tfoot"), ftr = el("tr");
      const s = (fn) => items.reduce((acc, it) => acc + fn(it), 0);
      add(ftr, [[t("common.sum")], [""], [s((it) => it.taken || 0), "right"], [s(soldOf), "right"],
        [s(retOf), "right"], [s(schwundOf), "right"], [eur(s(revOf)), "right"],
        [eur(s(shrValOf)), "right"], [eur(s(dbOf)), "right"]]);
      tfoot.appendChild(ftr); table.appendChild(tfoot);
    }
    doc.body.appendChild(table);
    w.focus(); w.print();
  };

  const items = useMemo(() => stock?.items || [], [stock]);
  const reconciled = settled;
  reconciledRef.current = reconciled;
  const sum = (fn) => items.reduce((s, it) => s + fn(it), 0);

  // Suche und Sortierung wirken NUR auf die Anzeige. Summen, Fußzeile und die Buchung der
  // Abrechnung laufen unverändert über ALLE Positionen (`items`) — es wird also nie nur der
  // gefilterte Teil abgerechnet.
  const ACC = {
    sku: (it) => it.sku || "",
    name: (it) => it.name || "",
    taken: (it) => it.taken || 0,
    on_fair: (it) => it.on_fair || 0,
    sold: soldOf,
    returned: retOf,
    schwund: schwundOf,
  };
  const toggleSort = (k) => {
    const dir = sort.key === k && sort.dir === "asc" ? "desc" : "asc";
    const acc = ACC[k];
    const snapshot = [...items].sort((a, b) => {
      const x = acc(a), y = acc(b);
      if (typeof x === "number" && typeof y === "number") return dir === "asc" ? x - y : y - x;
      return dir === "asc"
        ? String(x).localeCompare(String(y), "de", { numeric: true })
        : String(y).localeCompare(String(x), "de", { numeric: true });
    }).map(key);
    setSort({ key: k, dir });
    setOrder(snapshot);
  };
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = needle
      ? items.filter((it) => `${it.sku || ""} ${it.name || ""}`.toLowerCase().includes(needle))
      : items;
    if (order) {
      const rank = new Map(order.map((k, i) => [k, i]));
      list = [...list].sort((a, b) => (rank.get(key(a)) ?? 0) - (rank.get(key(b)) ?? 0));
    }
    return list;
  }, [items, q, order]);
  const sortProps = (k) => ({ sortId: k, active: sort.key === k, dir: sort.dir, onSort: toggleSort });
  const totSold = sum(soldOf);
  const totRet = sum(retOf);
  const totUmsatz = sum(revOf);
  const totSchwundQ = sum(schwundOf);
  const totSchwundVal = sum(shrValOf);
  const totDB = sum(dbOf);
  const savedTime = draft.updatedAt ? new Intl.DateTimeFormat(intlTag(locale), { hour: "2-digit", minute: "2-digit" }).format(new Date(draft.updatedAt)) : null;
  const DraftStatus = () => {
    if (!canEdit || closed || reconciled) return null;
    if (saveState === "saving") return (
      <span className="flex items-center gap-1 text-xs text-muted-foreground" data-testid="draft-status">
        <Loader2 className="h-3 w-3 animate-spin" /> {t("me.saving")}</span>);
    if (saveState === "dirty") return (
      <span className="flex items-center gap-1 text-xs text-muted-foreground" data-testid="draft-status">
        <Loader2 className="h-3 w-3 animate-spin" /> {t("me.unsavedShort")}</span>);
    if (saveState === "error") return (
      <span className="flex items-center gap-1 text-xs font-semibold text-destructive" data-testid="draft-status">
        <AlertTriangle className="h-3.5 w-3.5" /> {t("me.unsavedWarn")}</span>);
    if (saveState === "saved") return (
      <span className="flex items-center gap-1 text-xs text-muted-foreground" data-testid="draft-status">
        <Check className="h-3 w-3 text-primary" /> {t("me.draftSaved")}{savedTime ? ` ${savedTime}` : ""}
        {draft.by ? ` · ${draft.by}` : ""}</span>);
    return null;
  };
  return (
    <div className="space-y-4" data-testid="messe-takelist">
      <div className="rounded-md border border-border bg-card p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <Label className="flex items-center gap-1.5"><Truck className="h-4 w-4" /> {t("me.takelistSettle")}</Label>
          <div className="flex items-center gap-2">
            <DraftStatus />
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" onClick={printList} disabled={!hydrated} data-testid="print-takelist-btn"><Printer className="h-3.5 w-3.5 mr-1" /> {t("common.print")}</Button>
              {canEdit && !closed && <Button size="sm" variant="outline" onClick={addLine} disabled={!hydrated} data-testid="take-add-line"><Plus className="h-3.5 w-3.5 mr-1" /> {t("ord.colItems")}</Button>}
            </div>
          </div>
        </div>
        {!hydrated && <p className="text-xs text-muted-foreground" data-testid="takelist-loading">{t("common.loading")}</p>}
        {canEdit && !closed && hydrated && (
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={l.uid} className="flex items-center gap-2">
                <Select value={l.kind} onValueChange={(v) => updLine(i, "kind", v)}><SelectTrigger className="w-32" data-testid={`take-kind-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="variant">{t("bundle.chooseProduct")}</SelectItem><SelectItem value="bundle">{t("bundle.title")}</SelectItem><SelectItem value="messe_bundle">{t("messebundle.title")}</SelectItem></SelectContent></Select>
                <SearchSelect className="flex-1" testId={`take-ref-${i}`} placeholder={t("me.chooseItem")} value={l.ref} onChange={(v) => updLine(i, "ref", v)}
                  options={(l.kind === "bundle" ? bundles : l.kind === "messe_bundle" ? messeBundles : variants).map((x) => ({ value: x.id, label: optLabel(l.kind, x) }))} />
                <Input type="number" className="w-20" placeholder={t("common.quantity")} value={l.quantity} onChange={(e) => updLine(i, "quantity", e.target.value)} data-testid={`take-qty-${i}`} />
                <Button size="icon" variant="ghost" onClick={() => rmLine(i)} data-testid={`take-remove-line-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            ))}
            {lines.length === 0 && <p className="text-xs text-muted-foreground">{t("me.addLineHint")}</p>}
            {lines.length > 0 && <div className="flex justify-end"><Button onClick={book} disabled={busy} data-testid="take-book-btn"><Truck className="h-4 w-4 mr-1.5" /> {t("me.bookTake")}</Button></div>}
          </div>
        )}

        {items.length > 0 && hydrated && (<>
          <div className="flex items-center justify-between mt-4 mb-1 border-t border-border pt-3">
            <Label className="flex items-center gap-1.5"><ClipboardCheck className="h-4 w-4" />
              {reconciled ? t("me.settledActuals") : t("me.settleTitle")}</Label>
            {reconciled && <span className="text-xs text-muted-foreground" data-testid="settled-info">
              {t("me.settledInfo", { date: deDate(stock?.reconciled_at), by: stock?.reconciled_by_email || "—", value: eur(stock?.shrinkage_value || 0) })}</span>}
          </div>
          <div className="mb-2 flex items-center gap-3">
            <div className="relative w-full max-w-sm">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="settle-search"
                placeholder={t("me.filterPh")}
                className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring" />
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="settle-count">
              {q.trim() ? `${visible.length} / ${tp("plural.lines", items.length)}` : tp("plural.lines", items.length)}
            </span>
          </div>
          <MesseSettleTable visible={visible} items={items} reconciled={reconciled} canEdit={canEdit}
            closed={closed} busy={busy} entries={entries} onEnter={enter} sortProps={sortProps}
            keyOf={key} valOf={val} overbooked={overbooked} soldOf={soldOf} retOf={retOf}
            schwundOf={schwundOf} revOf={revOf} shrValOf={shrValOf} dbOf={dbOf}
            totals={{ sold: totSold, returned: totRet, schwundQty: totSchwundQ,
              revenue: totUmsatz, schwundValue: totSchwundVal, margin: totDB }}
            onEditTake={editTake} onRemoveTake={removeTake} />
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm border-t border-border pt-3">
            <span className="text-muted-foreground">{t("me.revenueSold")}: <b className="text-foreground font-mono">{eur(totUmsatz)}</b></span>
            <span className="text-muted-foreground">{t("me.soldPieces")} <b className="text-foreground font-mono">{totSold}</b></span>
            <span className="text-muted-foreground">{t("me.shrinkageValue")}: <b className="text-destructive font-mono" data-testid="settle-schwund-value">{eur(totSchwundVal)}</b></span>
            <span className="ml-auto text-muted-foreground">{t("common.margin")}: <b className="text-primary font-mono">{eur(totDB)}</b></span>
          </div>
          {canEdit && !closed && !reconciled && (
            <div className="mt-3 flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">{t("me.schwundHint")}</p>
              <div className="flex gap-1.5">
                {draft.version != null && <Button size="sm" variant="ghost" onClick={discardDraft} disabled={busy} data-testid="draft-discard-btn">{t("me.discardDraftBtn")}</Button>}
                <Button size="sm" variant="outline" onClick={() => settle(false)} disabled={busy} data-testid="settle-btn">{t("me.bookSettlement")}</Button>
                <Button size="sm" onClick={() => settle(true)} disabled={busy} data-testid="settle-close-btn">{t("me.settleClose")}</Button>
              </div>
            </div>
          )}
        </>)}
      </div>
    </div>
  );
}
